const w = globalThis || window;
import { $async } from "./Async.js";

/**
 * Utility class for common window operations.
 */
class WindowUtils {
  /**
   * Copies the specified text to the clipboard.
   *
   * @param {string} text - The text to copy to the clipboard.
   * @throws {Error} If there is an issue with copying the text to the clipboard.
   */
  copyToClipboard(text) {
    w.navigator.clipboard.writeText(text);
  }

  /**
   * Changes the CSS class of an element by its ID.
   * @param {string} id - The ID of the element.
   * @param {string} css - The new CSS class to apply.
   * @throws {Error} If the element with the given ID is not found.
   */
  changeCssById(id, css) {
    const element = document.getElementById(id);
    if (element) {
      element.className = css;
    }
  }

  /**
   * Updates a specific CSS property of an element by its ID.
   * @param {string} elementId - The ID of the element.
   * @param {string} propertyName - The CSS property name to update.
   * @param {string} value - The new value for the CSS property.
   * @throws {Error} If the element with the provided ID is not found.
   */
  updateStyleProperty(elementId, propertyName, value) {
    const element = document.getElementById(elementId);
    if (element) {
      element.style.setProperty(propertyName, value);
    }
  }

  /**
   * Changes a global CSS variable.
   * @param {string} name - The name of the CSS variable.
   * @param {string} newValue - The new value for the CSS variable.
   * @throws {Error} If unable to set the CSS variable.
   */
  changeGlobalCssVariable(name, newValue) {
    document.documentElement.style.setProperty(name, newValue);
  }

  /**
   * Opens a new window with the specified arguments.
   * @param {string|Object} args - The arguments for the window.open method.
   * @returns {Promise} A promise that resolves when the window is closed or an error occurs.
   * @throws {Error} If there is an issue opening the window.
   */
  open(args) {
    const win = window.open(args);
    return new Promise((resolver) => {
      win.onabort = resolver;
      win.onerror = resolver;
      win.onunload = resolver;
    });
  }

  /**
   * Shares content using the Web Share API.
   * @param {Object} args - The arguments for the navigator.share method.
   * @returns {Promise} A promise that resolves when the sharing is complete.
   * @throws {Error} If there is an issue with sharing the content.
   */
  share(args) {
    return w.navigator.share(args);
  }

  /**
   * Returns the user agent string of the browser.
   * @returns {string} The user agent string.
   * @throws {Error} If the user agent string is not available.
   */
  ua() {
    return w.navigator.userAgent;
  }

  /**
   * Scrolls the window to the top with a smooth animation.
   * @param {number} [top=0] - The top position to scroll to.
   * @throws {Error} If the scrollTo function is not available in the window object.
   */
  scrollTop(top = 0) {
    w.scrollTo({ top, behavior: "smooth" });
  }

  /**
   * Adds a CSS class to the HTML element and scrolls to the top.
   * @param {string} cssClassNames - The CSS class names to add.
   * @throws {Error} If the HTML element is not found.
   * @throws {Error} If there is an issue with setting the timeout.
   * @throws {Error} If there is an issue with scrolling to the top.
   */
  addClassToHtmlElement(cssClassNames) {
    const htmlElement = document.documentElement;
    if (htmlElement) {
      htmlElement.classList.add(cssClassNames);
      $async.setTimeout(() => this.scrollTop(), 0);
    }
  }

  /**
   * Removes a CSS class from the HTML element and scrolls to the top.
   * @param {string} cssClassNames - The CSS class names to remove.
   * @throws {Error} If the HTML element is not found.
   * @throws {TypeError} If the cssClassNames parameter is not a string.
   */
  removeClassFromHtmlElement(cssClassNames) {
    const htmlElement = document.documentElement;
    if (htmlElement) {
      htmlElement.classList.remove(cssClassNames);
      $async.setTimeout(() => this.scrollTop(), 50);
    }
  }

  /**
   * Captures the content size of an element with the specified class name.
   * @param {string} [previousPageRoute=null] - The previous page route, if any.
   * @returns {Object} The captured content size and other details.
   * @throws {Error} If the content element with the specified class name is not found.
   */
  captureContentSize(previousPageRoute = null) {
    const contentElement = document.querySelector(".transition-container-in");
    if (!contentElement) {
      return null;
    }

    const { clientHeight: height, clientWidth: width } = contentElement;
    const pageRoute = previousPageRoute || document.location.pathname;

    return {
      key: pageRoute,
      height,
      width,
      element: contentElement,
      createdAt: new Date(),
    };
  }

  /**
   * Returns a promise that resolves when the DOM is fully loaded.
   * @returns {Promise} A promise that resolves when the DOM is ready.
   * @throws {Error} If there is an issue with setting timeout or adding event listener.
   */
  ready() {
    return new Promise((resolve) => {
      if (
        document.readyState === "complete" ||
        document.readyState === "interactive"
      ) {
        $async.setTimeout(resolve, 1);
      } else {
        document.addEventListener("DOMContentLoaded", resolve);
      }
    });
  }

  disableScrollbars() {
    w.overlayScrollbarsInstanceUtils.disable();
  }

  enableScrollbars() {
    w.overlayScrollbarsInstanceUtils.enable();
  }

  /**
   * Updates the CSS class of the header element based on the provided options.
   *
   * This function searches for a header element with a specific class and replaces
   * the original CSS class with a new one if they are different. If the header element
   * is not found or if the original and new CSS classes are the same, the function exits
   * without making any changes.
   *
   * @param {Object} updateNavOptions - The options for updating the navigation CSS class.
   * @param {string} updateNavOptions.originalCssClass - The original CSS class to be replaced.
   * @param {string} updateNavOptions.newCssClass - The new CSS class to apply.
   *
   * @returns {void} This function does not return a value.
   *
   * @example
   * // Example usage:
   * updateNavCssClass({
   *   originalCssClass: 'old-class',
   *   newCssClass: 'new-class'
   * });
   *
   * @throws {Error} Throws an error if updateNavOptions is not provided or is not an object.
   */
  updateNavCssClass(updateNavOptions) {
    const headerElement = document.querySelector("header.bg-gradient-to-b");

    if (!headerElement) {
      return;
    }

    if (updateNavOptions.originalCssClass == updateNavOptions.newCssClass) {
      return;
    }

    var classList = headerElement.className.replace(
      updateNavOptions.originalCssClass,
      updateNavOptions.newCssClass,
    );

    headerElement.className = classList;
  }

  get packageLoader() {
    return window.packageLoader;
  }

  setValueOrInnerText(element, newValue) {
    if (
      element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement ||
      element instanceof HTMLSelectElement
    ) {
      if (element.type === "checkbox" || element.type === "radio") {
        element.checked = newValue === "true" || newValue === element.value;
      } else if (element instanceof HTMLSelectElement) {
        for (let i = 0; i < element.options.length; i++) {
          if (element.options[i].value === newValue) {
            element.selectedIndex = i;
            break;
          }
        }
        // Dispatch change event for React-like frameworks
        element.dispatchEvent(new Event("change", { bubbles: true }));
      } else {
        element.value = newValue;
      }
      // Dispatch input event for two-way binding
      element.dispatchEvent(new Event("input", { bubbles: true }));
    } else {
      element.innerText = newValue;
    }
  }

  getValueOrInnerText(element) {
    if (
      element instanceof HTMLTextAreaElement ||
      element instanceof HTMLInputElement ||
      element instanceof HTMLSelectElement
    ) {
      if (element.type === "checkbox" || element.type === "radio") {
        return element.checked ? element.value : null;
      }
      if (element instanceof HTMLSelectElement) {
        return element.options[element.selectedIndex]?.value ?? element.value;
      }
      return element.value ?? null;
    }
    return element.innerText ?? element.textContent ?? null;
  }

  /**
   * Retrieves the current inner width of the browser window.
   *
   * This function accesses the `innerWidth` property of the `window` object,
   * which represents the width of the viewport in pixels, excluding the vertical
   * scrollbar if it is visible.
   *
   * @returns {number} The inner width of the browser window in pixels.
   *
   * @example
   * const width = getInnerWidth();
   * console.log(`The inner width of the window is: ${width}px`);
   */
  getInnerWidth() {
    return window.innerWidth;
  }
}

// Create a singleton instance of WindowUtils
const $windowUtils = new WindowUtils();

export { $windowUtils, WindowUtils };
